<!--
SubscriptionTermForm — authoring one VERSION of one plan's agreement, and
its language variants.

Two halves, because they are two different acts. The top identifies the
version: which plan, which title, which version string. The bottom is the
wording, one language at a time, chosen with the language selector — exactly
the shape the channel templates console already uses.

The plan selector reads the CATALOGUE from the endpoint that owns it, so it
offers the plan by name and never a second, drifting copy of the list.

PLAN AND VERSION ARE NOT PER LANGUAGE. A plan has one agreement in force, and
the variants are translations of it. The plan is locked once the version
exists: changing it would silently move the agreement to a different product,
taking any signatures with it.

The language selector lists EVERY supported language, marking the ones with no
text yet, so a missing translation is visible rather than something the author
has to remember. Publishing with pt-BR alone is fine; the reader chain covers
the rest.
-->
<template>
  <form class="subscription-term-form" data-testid="subscription-term-form" @submit.prevent="handleSubmit">
    <div class="form-grid">
      <div class="form-group">
        <select-list
          v-model="selectedPlanId"
          elem-id="subscription-term-plan"
          :label="t('Plano')"
          :itens-select="planOptions"
          :auto-select-first="false"
          :disabled="isEditing"
          data-testid="subscription-term-form-plan"
        />
        <small v-if="isEditing" class="form-hint">
          {{ t('O plano não muda depois de criado — crie outra versão.') }}
        </small>
      </div>

      <div class="form-group">
        <label class="form-label">{{ t('Versão') }} *</label>
        <input-text
          v-model="model.versao"
          elem-id="subscription-term-versao"
          :required="true"
          :placeholder="t('Ex: 1.0')"
          data-testid="subscription-term-form-versao"
        />
      </div>

      <div class="form-group form-group-full">
        <label class="form-label">{{ t('Título') }} *</label>
        <input-text
          v-model="model.titulo"
          elem-id="subscription-term-titulo"
          :required="true"
          :placeholder="t('Ex: Termos de Assinatura')"
          data-testid="subscription-term-form-titulo"
        />
      </div>
    </div>

    <section v-if="isEditing" class="variant-section" data-testid="subscription-term-variants">
      <div class="variant-bar">
        <select-list
          v-model="selectedLanguage"
          elem-id="subscription-term-language"
          :label="t('Idioma do texto')"
          :itens-select="languageOptions"
          :auto-select-first="false"
          data-testid="subscription-term-form-language"
        />
      </div>

      <p v-if="!currentVariant" class="form-hint" data-testid="subscription-term-no-variant">
        {{ t('Ainda não existe texto em') }} {{ selectedLanguage }}.
        {{ t('Escreva abaixo e salve para criar a variante. Enquanto ela não existir, quem lê nesse idioma recebe a tradução mais próxima.') }}
      </p>

      <label class="form-label">{{ t('Texto do termo') }} *</label>
      <InputRichText
        v-model="variantBody"
        data-testid="subscription-term-form-conteudo"
      />
      <small class="form-hint">
        {{ t('Este é o texto que a franquia assina. Um texto de exemplo publicado vira prova de um acordo que ninguém escreveu.') }}
      </small>
    </section>

    <p v-else class="form-hint" data-testid="subscription-term-save-first">
      {{ t('Salve a versão para começar a escrever o texto em cada idioma.') }}
    </p>

    <p v-if="validationMessage" class="form-error" data-testid="subscription-term-form-error">
      <i class="pi pi-exclamation-triangle"></i>
      {{ validationMessage }}
    </p>
  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import InputText from '../../../inputs/InputText.vue'
import SelectList from '../../../inputs/SelectList.vue'
import InputRichText from '../../../inputs/InputRichText.vue'
import { useSubscriptionTerms } from '../../../composables/useSubscriptionTerms'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import type { SubscriptionTerms_VariantRow } from '../../../contracts/SubscriptionTerms_VariantRow'

const DEFAULT_LANGUAGE = 'pt-BR'

export interface SubscriptionTermFormData {
  plan_id: number
  titulo: string
  versao: string
}

const props = defineProps<{
  isEditing?: boolean
  variants?: SubscriptionTerms_VariantRow[]
}>()

const emit = defineEmits<{
  save: [data: SubscriptionTermFormData]
  'save-variant': [payload: { language: string; conteudo: string }]
  'validation-failed': []
}>()

const { t } = useI18n()
const { languages } = useSubscriptionTerms()
// The plan catalogue has ONE owner. A second copy here is how a selector ends
// up naming plans the catalogue no longer has.
const { plans, listPlans } = useSubscriptionAdmin()

const model = defineModel<SubscriptionTermFormData>({
  default: () => ({ plan_id: 0, titulo: '', versao: '' }),
})

const selectedLanguage = ref(DEFAULT_LANGUAGE)
const variantBody = ref('')
const validationMessage = ref<string | null>(null)

const isEditing = computed(() => props.isEditing === true)
const variants = computed(() => props.variants ?? [])

/** SelectList speaks strings; the model carries the numeric plan id. */
const selectedPlanId = computed<string>({
  get: () => (model.value.plan_id > 0 ? String(model.value.plan_id) : ''),
  set: (value) => {
    model.value.plan_id = Number(value) || 0
  },
})

/**
 * One option per PLAN, not per price row.
 *
 * GET /subscriptions/plans expands the catalogue by price: a plan with a BRL
 * row on gatewaytest and another on asaas comes back twice, and the selector
 * showed "Plano Mensal (seed)" twice with no way to tell them apart. The
 * agreement hangs off the plan, so the gateway a price happens to use is not a
 * distinction this screen has any business making.
 */
const planOptions = computed(() => {
  const byId = new Map<number, string>()
  for (const p of plans.value) {
    if (!byId.has(p.id)) {
      byId.set(p.id, p.name ?? `#${p.id}`)
    }
  }

  return [...byId].map(([id, name]) => ({ ID: String(id), Name: name }))
})

const currentVariant = computed(
  () => variants.value.find((v) => v.language === selectedLanguage.value) ?? null,
)

/**
 * Every supported language, with the ones carrying no text marked. A language
 * without a variant does NOT disappear from the list — that is how an author
 * discovers it is missing and can write it.
 */
const languageOptions = computed(() => {
  const written = new Set(variants.value.map((v) => v.language))

  return languages.value.map((l) => ({
    ID: l,
    Name: written.has(l) ? l : `${l} — ${t('sem texto')}`,
  }))
})

// Switching language swaps the body under the editor, so the author is never
// editing one language while looking at another's label.
watch(
  [selectedLanguage, () => props.variants],
  () => {
    variantBody.value = currentVariant.value?.conteudo ?? ''
  },
  { immediate: true },
)

onMounted(() => {
  void listPlans().catch(() => {
    // The selector simply stays empty; the console's own error surface reports
    // the failure. Swallowing it here would be the only place it went quiet.
  })
})

function handleSubmit(): void {
  submit()
}

function submit(): boolean {
  const data = model.value

  if (!data.plan_id) {
    validationMessage.value = t('Escolha o plano deste termo.')
    emit('validation-failed')
    return false
  }

  if (!data.titulo.trim() || !data.versao.trim()) {
    validationMessage.value = t('Preencha título e versão.')
    emit('validation-failed')
    return false
  }

  // While creating, there is no version to hang a variant on yet.
  if (!isEditing.value) {
    validationMessage.value = null
    emit('save', { ...data })
    return true
  }

  if (!variantBody.value.trim()) {
    validationMessage.value = t('Escreva o texto do termo neste idioma.')
    emit('validation-failed')
    return false
  }

  validationMessage.value = null
  emit('save', { ...data })
  emit('save-variant', { language: selectedLanguage.value, conteudo: variantBody.value })
  return true
}

defineExpose({ submit })
</script>

<style scoped>
.subscription-term-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group-full {
  grid-column: 1 / -1;
}

.form-label {
  font-weight: 500;
  font-size: 14px;
}

.form-hint {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 12px;
}

.variant-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--p-content-border-color, #e5e7eb);
}

.variant-bar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--p-red-500);
  font-size: 13px;
  margin: 0;
}
</style>
